import { HideObjectType } from '../../../../@types/enums';

import { LAYER_OPTIONS } from '../../../../Constants';

import { useViewerStore } from '../../../../store/store';

import { logger } from '../../../../services/loggingService';

import { siteCalculationState, SHADE_CALCULATIONS } from '../../../../services/siteCalculationState';

import {
    applyCategoryLayoutInViewer,
    awaitAuthoritativeModuleGraph,
    isSnapshotApplicableToViewer,
    resolveApplyCategoryLabel,
} from './utils/layout';

import {

    logAiDesignOpenStart,

    logAiDesignResolveInputs,

    logAiDesignOpenEnd,

    verifyAppliedDesign,

} from './utils/ai-layout';

import {

    beginTreeScenarioLayoutSuppress,

    clearPendingUnghostAlert,

    endTreeScenarioLayoutSuppress,

} from '../Three-JS-Viewer/Controllers/TreeImpactEligibility';

import { restoreModulesLayoutFromSnapshot } from '../Three-JS-Viewer/Controllers/DefaultLayoutControl';

import { SetModuleGraph } from '../Three-JS-Viewer/Controllers/ModuleControl';

import { restoreAllTrees } from '../../../../services/aiModuleFillingService';

import { SetDisplayShadePoints } from '../Three-JS-Viewer/Controllers/RefreshShade';

import { type CategoryLayoutCandidate } from './utils/ai-layout';

import type { SavedRoofModulesLayout } from '../../../../@types/types';



type RunUseThisLayoutArgs = {

    scenarioIndex: number;

    useLayoutKey?: string;

    scenariosFromModal?: any[];

    selectedRoofCombinationKey: string;

    setSelectedRoofCombinationKey: (next: string) => void;

    sceneViewer: any;

    restoreDefaultLayoutFromSnapshot: () => Promise<void>;

    roofCombinationsFromAi: Array<{ name: string; roofFaceIds?: string[] }>;

    layoutCandidatesByCategory?: Record<string, Array<{ key: string; roofFaceIds?: string[]; dir?: 'port' | 'land' | 'both' | null; minEff?: number | null }>>;

    categoryMainLayoutByCategory?: Record<string, CategoryLayoutCandidate>;

    defaultMaxfillRoofIds?: string[];

    bestRoofsForLayout: { roofFaceIds?: string[] };

    categoryLabel?: string;

    simulateLayoutForRoofIds: (

        roofFaceIds: string[],

        opts?: { dir?: 'port' | 'land' | 'both' | null; minEff?: number | null },

    ) => Promise<any>;

    applyRoofSetInViewer?: (

        roofFaceIds: string[],

        opts?: {

            dir?: 'port' | 'land' | 'both' | null;

            minEff?: number | null;

            deferShadeCalc?: boolean;

        },

    ) => Promise<void>;

    waitForSceneIdle: () => Promise<void>;

    treeScenariosByLayoutKey: Record<string, any[]>;

    savedAIDesigns: any[];

    applyScenarioLayout: (scenarioIndex: number) => Promise<void>;

    setTreeScenariosByLayoutKey: (next: (prev: Record<string, any[]>) => Record<string, any[]>) => void;

    setApplyingScenarioIndex: (next: number | null) => void;

    setApplyingTreeLayoutFromScenario: (next: boolean) => void;

};



export async function runUseThisLayoutAction(args: RunUseThisLayoutArgs): Promise<void> {

    const {

        scenarioIndex,

        useLayoutKey,

        scenariosFromModal,

        selectedRoofCombinationKey,

        setSelectedRoofCombinationKey,

        sceneViewer,

        restoreDefaultLayoutFromSnapshot: _restoreDefaultLayoutFromSnapshot,

        roofCombinationsFromAi,

        layoutCandidatesByCategory = {},

        categoryMainLayoutByCategory = {},

        defaultMaxfillRoofIds = [],

        bestRoofsForLayout,

        categoryLabel,

        simulateLayoutForRoofIds,

        applyRoofSetInViewer,

        waitForSceneIdle,

        treeScenariosByLayoutKey,

        savedAIDesigns,

        applyScenarioLayout,

        setTreeScenariosByLayoutKey,

        setApplyingScenarioIndex,

        setApplyingTreeLayoutFromScenario,

    } = args;



    beginTreeScenarioLayoutSuppress();

    clearPendingUnghostAlert();

    setApplyingScenarioIndex(scenarioIndex);

    // Exact placement we expect on the roof — used for post-apply verification + auto-correct.

    let appliedModulesLayout: SavedRoofModulesLayout | undefined;

    try {

        const layoutKeyForScenario = useLayoutKey || selectedRoofCombinationKey || 'default';
        const resolvedCategoryLabel = resolveApplyCategoryLabel(layoutKeyForScenario, categoryLabel);

        const selectionKey =

            layoutKeyForScenario === 'default' ? 'default' : resolvedCategoryLabel || layoutKeyForScenario;

        const scenarioLookupKey = resolvedCategoryLabel || layoutKeyForScenario;

        const matchedSavedDesign = savedAIDesigns.find(

            (d) =>

                d.layoutKey === layoutKeyForScenario ||

                d.layoutKey === scenarioLookupKey ||

                (resolvedCategoryLabel && d.categoryId === resolvedCategoryLabel),

        );

        logAiDesignOpenStart({

            source: 'wishlist/runUseThisLayoutAction',

            layoutKey: layoutKeyForScenario,

            categoryLabel: resolvedCategoryLabel,

            selectionKey,

        });

        setApplyingTreeLayoutFromScenario(true);

        if (selectedRoofCombinationKey !== selectionKey) {

            setSelectedRoofCombinationKey(selectionKey);

        }



        let scenariosForLayout =

            Array.isArray(scenariosFromModal) && scenariosFromModal.length > 0

                ? scenariosFromModal

                : treeScenariosByLayoutKey?.[scenarioLookupKey] ?? treeScenariosByLayoutKey?.[layoutKeyForScenario];

        if (!Array.isArray(scenariosForLayout) || scenariosForLayout.length === 0) {

            if (

                matchedSavedDesign?.treeScenarios &&

                Array.isArray(matchedSavedDesign.treeScenarios) &&

                matchedSavedDesign.treeScenarios.length > 0

            ) {

                scenariosForLayout = matchedSavedDesign.treeScenarios;

            }

        }

        if (Array.isArray(scenariosForLayout) && scenariosForLayout.length > scenarioIndex) {

            useViewerStore.setState({

                aiModuleFillingScenarios: scenariosForLayout,

                aiModuleFillingDesignId: scenarioLookupKey,

            } as any);

        }



        if (sceneViewer) {

            try {

                sceneViewer.removeViewerAlert?.();

            } catch {

                // ignore

            }

            try {

                useViewerStore.setState({ isHideAllGhostTrees: false } as any);

                sceneViewer.setVisibleGroup?.(LAYER_OPTIONS.MODEL.TREE, true);

                sceneViewer.hideObject?.(HideObjectType.GhostTree, false);

            } catch {

                // ignore visibility errors

            }

            logAiDesignResolveInputs(resolvedCategoryLabel);

                const viewerState = useViewerStore.getState() as any;

                appliedModulesLayout = await applyCategoryLayoutInViewer({

                    viewer: sceneViewer,

                    layoutKey: layoutKeyForScenario,

                    categoryLabel: resolvedCategoryLabel,

                    categoryMainLayoutByCategory: viewerState.categoryMainLayoutByCategory ?? categoryMainLayoutByCategory,

                    layoutCandidatesByCategory,

                    variantsByCategory: viewerState.aiCategoryLayoutVariants,

                    layoutModulesLayoutByKey: viewerState.layoutModulesLayoutByKey,

                    roofCombinationsFromAi,

                    bestRoofsForLayout,

                    defaultMaxfillRoofIds,

                    simulateLayoutForRoofIds,

                    applyRoofSetInViewer: applyRoofSetInViewer ?? (async () => {}),

                });



            await waitForSceneIdle();

        }



        if (Array.isArray(scenariosForLayout) && scenariosForLayout.length > scenarioIndex) {

            await applyScenarioLayout(scenarioIndex);

        } else if (sceneViewer) {

            restoreAllTrees(sceneViewer);

            await SetModuleGraph(sceneViewer);

            SetDisplayShadePoints(sceneViewer, undefined);

        }



        try {

            const stateNow = useViewerStore.getState() as any;

            const currentProduction =

                typeof stateNow.actualWattage === 'number' && Number.isFinite(stateNow.actualWattage)

                    ? stateNow.actualWattage

                    : 0;



            if (Array.isArray(scenariosForLayout) && scenariosForLayout.length > scenarioIndex) {

                const updatedScenariosForLayout = scenariosForLayout.map((s: any, idx: number) =>

                    idx === scenarioIndex ? { ...s, actualWattage: currentProduction } : s,

                );

                setTreeScenariosByLayoutKey((prev) => ({

                    ...prev,

                    [layoutKeyForScenario]: updatedScenariosForLayout,

                }));

                useViewerStore.setState((prev: any) => ({

                    ...prev,

                    aiModuleFillingScenarios: updatedScenariosForLayout,

                    aiModuleFillingDesignId: scenarioLookupKey,

                }));

            }

        } catch {

            // ignore metric capture failures when applying scenario layout

        }



        // Verify the viewer matches the opened design; auto-correct from the exact

        // placement snapshot when we have one and the live module count diverges.

            const check = verifyAppliedDesign({

                layoutKey: layoutKeyForScenario,

                categoryLabel: resolvedCategoryLabel,

                expectedModulesLayout: appliedModulesLayout,

            });

            if (check.mismatch && appliedModulesLayout?.length && sceneViewer) {
                const snapshotStillApplicable = isSnapshotApplicableToViewer(
                    sceneViewer,
                    layoutKeyForScenario,
                    resolvedCategoryLabel,
                    appliedModulesLayout,
                );
                if (snapshotStillApplicable) {
                    console.warn('[AI-DESIGN-FLOW] auto-correcting viewer from exact placement snapshot');
                    await restoreModulesLayoutFromSnapshot(sceneViewer, appliedModulesLayout, {
                        skipShadeRefresh: true,
                    });
                    await waitForSceneIdle();
                }
            }

    } catch (e) {

        logger?.error?.('handleUseThisLayout failed', e);

    } finally {

        endTreeScenarioLayoutSuppress();

        try {

            if (appliedModulesLayout?.length && sceneViewer) {
                await awaitAuthoritativeModuleGraph(sceneViewer);
            } else {
                siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
                await siteCalculationState.useCalculations(SHADE_CALCULATIONS);
            }

        } catch {

            // ignore shade flush errors

        }

        logAiDesignOpenEnd();

        setApplyingScenarioIndex(null);

        setApplyingTreeLayoutFromScenario(false);

    }

}

